import axios, { AxiosError } from "axios";
import camelCase from "camelcase-keys";
import snakeCase from "snakecase-keys";

import { ApiResponseBase } from "@hunkware/shared/types";

import { errorsApi } from "../../../features/error-boundary";
import { API_URL } from "../../configs";
// Lazy import BugsnagSafe to prevent loading during critical path
// Only import when actually needed (on errors)

declare module "axios" {
  export interface AxiosRequestConfig {
    customOptions?: {
      skipErrorHandle?: boolean;
      skipRequestBodyCasing?: boolean;
    };
  }
}

export const fetcher = axios.create({
  baseURL: API_URL + "/public/website",
});

fetcher.interceptors.request.use((request) => {
  if (
    request.data &&
    !(request.data instanceof Blob || request.data instanceof FormData) &&
    !request.customOptions?.skipRequestBodyCasing
  ) {
    request.data = snakeCase(request.data, { deep: true, exclude: [/\.|\//] });
  }

  if (request.params) {
    request.params = snakeCase(request.params, {
      deep: true,
      exclude: [/\.|\//],
    });
  }

  return request;
});

fetcher.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      !(response.data instanceof Blob) &&
      typeof response.data === "object"
    ) {
      response.data = camelCase(response.data, { deep: true });
    }

    // Breadcrumbs are non-critical - skip them to avoid loading Bugsnag during critical path
    // They will be available once Bugsnag is initialized (on first error)

    return response;
  },

  (error: AxiosError<ApiResponseBase & any>) => {
    const method = error.response?.config?.method;
    const status = error.response?.status;
    const statusText = error.response?.statusText;

    const metaStatus = error.response?.data?.meta?.status;
    const errorName = `${status} (${metaStatus?.name || statusText})`;
    const isGetRequest = method?.toUpperCase() === "GET";

    const firstMetaError = error.response?.data?.meta?.errors?.find(
      (item: any) => item.http_code !== 200,
    );

    if (firstMetaError) {
      let errorMsg = "";

      const seriesId = error.response?.data?.meta?.series;
      if (seriesId) {
        errorMsg = `${seriesId}: `;
      }

      errorMsg += firstMetaError?.message;
      // Lazy load BugsnagSafe only when we have an actual error
      import("../../utils/bugsnag-safe").then(({ BugsnagSafe }) => {
        BugsnagSafe.notify(new Error(errorMsg));
      });
    }

    if (status && isGetRequest) {
      if (error.config.customOptions?.skipErrorHandle) {
        throw error;
      }

      if (status === 404) {
        return errorsApi.throw({
          name: errorName,
          message: "Oops. The page you were looking for Was not found.",
          cause: metaStatus.description,
        });
      }

      if (status >= 500) {
        return errorsApi.throw({
          name: errorName,
          message: "Oops! Something went wrong!",
          cause: metaStatus.description,
        });
      }
    }

    return Promise.reject(error);
  },
);
